import React, { createContext, useContext, useEffect, useState } from 'react';
import { database } from '../../book/src/firebase';
import { ref, onValue, off, onChildChanged, push, update, remove, get } from 'firebase/database';
import { useAuth } from './AuthContext';

const PunchCardsContext = createContext();

export function normalizePunchCardIds(punchCardsUsed) {
  if (!punchCardsUsed) return [];
  if (Array.isArray(punchCardsUsed)) {
    return punchCardsUsed
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.punchCardId || item.id || null;
        return null;
      })
      .filter(Boolean);
  }
  if (typeof punchCardsUsed === 'object') {
    return Object.keys(punchCardsUsed);
  }
  return [];
}

export function isPunchCardEligibleForApt(card, aptTypeId) {
  if (!card || card.archivedTimestamp) return false;

  const used = card.quantityUsed ?? 0;
  const total = card.cardQuantity ?? 0;
  if (used >= total) return false;

  if (card.expirationTimestamp && Number(card.expirationTimestamp) < Date.now()) {
    return false;
  }

  if (!aptTypeId) return true;

  const config = card.supportedAptsConfig || {};
  if (config.supportsAll !== false) return true;

  const ids = config.supportedAptIds || [];
  return ids.includes(aptTypeId);
}

export function normalizeEventIds(eventList) {
  if (!eventList) return [];
  if (Array.isArray(eventList)) {
    return eventList
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') return item.eventId || item.id || null;
        return null;
      })
      .filter(Boolean);
  }
  if (typeof eventList === 'object') {
    return Object.keys(eventList);
  }
  return [];
}

function flattenActivePunchCards(data) {
  if (!data) return [];
  const cards = [];
  Object.entries(data).forEach(([uid, userCards]) => {
    if (!userCards || typeof userCards !== 'object') return;
    Object.entries(userCards).forEach(([punchCardId, card]) => {
      if (!card || typeof card !== 'object') return;
      cards.push({
        ...card,
        uid,
        punchCardId,
        quantityUsed: card.quantityUsed ?? 0,
        eventList: normalizeEventIds(card.eventList),
      });
    });
  });
  return cards;
}

function flattenSavedPunchCards(data) {
  if (!data) return [];
  return Object.entries(data).map(([id, template]) => ({
    id,
    ...template,
  }));
}

function processPunchCardTagsSnapshot(snapshot, setPunchCardTags, setHiddenPunchCardTagsKeys) {
  const info = snapshot.val();
  let transformedTags = null;

  if (info) {
    transformedTags = Object.keys(info)
      .map((key) => ({
        value: key,
        label: info[key].name,
        color: info[key].color,
        isHidden: info[key].isHidden ?? false,
        isEditable: info[key].isEditable ?? true,
      }))
      .sort((a, b) => {
        const colorA = a.color.replace('#', '').toLowerCase();
        const colorB = b.color.replace('#', '').toLowerCase();
        return colorA.localeCompare(colorB);
      });
  }

  if (transformedTags?.length > 0) {
    const hiddenTags = transformedTags.filter((tag) => tag.isHidden);
    const hiddenTagsKeys = hiddenTags.reduce((obj, tag) => {
      obj[tag.value] = true;
      return obj;
    }, {});
    setHiddenPunchCardTagsKeys(hiddenTagsKeys);
  } else {
    setHiddenPunchCardTagsKeys({});
  }

  setPunchCardTags(transformedTags);
}

export const PunchCardsProvider = ({ children }) => {
  const { bizId, isFeatureEnabled } = useAuth();
  const [punchCards, setPunchCards] = useState([]);
  const [savedPunchCards, setSavedPunchCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [punchCardTags, setPunchCardTags] = useState(null);
  const [punchCardsTagsCount, setPunchCardsTagsCount] = useState({});
  const [hiddenPunchCardTagsKeys, setHiddenPunchCardTagsKeys] = useState({});

  const punchCardsEnabled = isFeatureEnabled('punchCards');

  useEffect(() => {
    if (!bizId || !punchCardsEnabled) {
      setPunchCards([]);
      setSavedPunchCards([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const activeRef = ref(database, `/biz/punchcards/${bizId}/users/active`);
    const savedRef = ref(database, `/biz/punchcards/${bizId}/savedPunchCards`);

    const activeListener = onValue(
      activeRef,
      (snapshot) => {
        setPunchCards(flattenActivePunchCards(snapshot.val()));
        setLoading(false);
      },
      () => {
        setPunchCards([]);
        setLoading(false);
      },
    );

    const savedListener = onValue(
      savedRef,
      (snapshot) => {
        setSavedPunchCards(flattenSavedPunchCards(snapshot.val()));
      },
      () => {
        setSavedPunchCards([]);
      },
    );

    return () => {
      off(activeRef, 'value', activeListener);
      off(savedRef, 'value', savedListener);
    };
  }, [bizId, punchCardsEnabled]);

  useEffect(() => {
    if (!bizId || !punchCardsEnabled) {
      setPunchCardTags(null);
      setHiddenPunchCardTagsKeys({});
      return undefined;
    }

    const dbRef = ref(database, `/biz/tags/${bizId}/punchcards`);

    const handleSnapshot = (snapshot) => {
      if (snapshot.exists()) {
        processPunchCardTagsSnapshot(snapshot, setPunchCardTags, setHiddenPunchCardTagsKeys);
      } else {
        setPunchCardTags(null);
        setHiddenPunchCardTagsKeys({});
      }
    };

    const handleChildChanged = () => {
      get(dbRef).then((fullSnapshot) => {
        if (fullSnapshot.exists()) {
          processPunchCardTagsSnapshot(fullSnapshot, setPunchCardTags, setHiddenPunchCardTagsKeys);
        }
      });
    };

    onValue(dbRef, handleSnapshot, { onlyOnce: false });
    onChildChanged(dbRef, handleChildChanged, { onlyOnce: false });

    return () => {
      off(dbRef, 'value');
      off(dbRef, 'child_changed');
    };
  }, [bizId, punchCardsEnabled]);

  useEffect(() => {
    const counterObject = {};

    if (punchCardTags?.length > 0 && punchCards?.length > 0) {
      punchCardTags.forEach((tag) => {
        counterObject[tag.value] = 0;
      });

      punchCards.forEach((card) => {
        if (card.tags?.length > 0) {
          card.tags.forEach((tag) => {
            const tagKey = Object.keys(tag)[0];
            if (counterObject[tagKey] !== undefined) {
              counterObject[tagKey] += 1;
            }
          });
        }
      });

      setPunchCardsTagsCount(counterObject);
    } else if (punchCardTags?.length > 0) {
      punchCardTags.forEach((tag) => {
        counterObject[tag.value] = 0;
      });
      setPunchCardsTagsCount(counterObject);
    } else {
      setPunchCardsTagsCount({});
    }
  }, [punchCardTags, punchCards]);

  const getPunchCardAvailableTags = (tagsCatalog, cardTags) => {
    try {
      if (!tagsCatalog) return [];

      const cardTagKeys = cardTags ? cardTags.map((obj) => Object.keys(obj)[0]) : [];

      return tagsCatalog.reduce((acc, tag) => {
        if (!cardTagKeys.includes(tag.value)) {
          acc.push({ [tag.value]: 1 });
        }
        return acc;
      }, []);
    } catch (err) {
      console.log(err);
      return [];
    }
  };

  const updatePunchCardTags = (uid, punchCardId, tagId, tags, operation) => {
    if (!bizId || !uid || !punchCardId) return;

    const currentTags = tags || [];
    let filteredTags;
    const timeAdded = new Date();

    if (operation === 'delete') {
      filteredTags = currentTags.filter((tag) => !(tagId in tag));
    } else {
      filteredTags = [...currentTags, { [tagId]: timeAdded.getTime() }];
    }

    const dbRef = ref(
      database,
      `/biz/punchcards/${bizId}/users/active/${uid}/${punchCardId}`,
    );

    try {
      update(dbRef, { tags: filteredTags });
    } catch (error) {
      console.error('Failed to update punch card tag', error);
    }
  };

  const addPunchCardTag = (tagName, tagColor) => {
    if (!bizId) return;
    const timeAdded = new Date();
    const dbRef = ref(database, `/biz/tags/${bizId}/punchcards`);

    try {
      const tagRef = push(dbRef, {
        createdTimestamp: timeAdded.getTime(),
        name: tagName,
        color: tagColor,
      });
      return tagRef.key;
    } catch (error) {
      console.error('Failed to add punch card tag', error);
    }
  };

  const modifyPunchCardTag = async (operation, tagId, tagName = null, tagColor = null) => {
    if (!bizId || !tagId) return;
    const dbRef = ref(database, `/biz/tags/${bizId}/punchcards/${tagId}`);

    try {
      if (operation === 'edit') {
        if (tagName === null || tagColor === null) {
          console.error('Both tag name and color must be provided for editing.');
          return;
        }
        await update(dbRef, { name: tagName, color: tagColor });
      } else if (operation === 'delete') {
        await update(dbRef, { name: tagName, color: tagColor, isHidden: true });
      } else {
        console.error('Invalid operation. Please specify "edit" or "delete".');
      }
    } catch (error) {
      console.error(`Failed to ${operation} punch card tag`, error);
    }
  };

  const getPunchCard = (uid, punchCardId) => {
    if (!uid || !punchCardId) return null;
    return punchCards.find((card) => card.uid === uid && card.punchCardId === punchCardId) || null;
  };

  const createPunchCard = async (uid, data) => {
    if (!bizId || !uid) throw new Error('Missing bizId or uid');
    const dbRef = ref(database, `/biz/punchcards/${bizId}/users/active/${uid}`);
    const newRef = push(dbRef);
    const payload = {
      timestampCreated: Date.now(),
      quantityUsed: 0,
      eventList: [],
      supportedAptsConfig: data.supportedAptsConfig || {
        supportsAll: true,
        supportedAptIds: [],
      },
      cardQuantity: Number(data.cardQuantity) || 1,
    };
    if (data.expirationTimestamp) {
      payload.expirationTimestamp = data.expirationTimestamp;
    }
    await update(newRef, payload);
    return newRef.key;
  };

  const updatePunchCard = async (uid, punchCardId, patch) => {
    if (!bizId || !uid || !punchCardId) throw new Error('Missing ids');
    const { eventList, timestampCreated, createTimestamp, ...safePatch } = patch || {};
    const dbRef = ref(database, `/biz/punchcards/${bizId}/users/active/${uid}/${punchCardId}`);
    const updates = {};
    if (safePatch.supportedAptsConfig !== undefined) {
      updates.supportedAptsConfig = safePatch.supportedAptsConfig;
    }
    if (safePatch.cardQuantity !== undefined) {
      updates.cardQuantity = Number(safePatch.cardQuantity);
    }
    if (safePatch.quantityUsed !== undefined) {
      updates.quantityUsed = Math.max(0, Number(safePatch.quantityUsed) || 0);
    }
    if (safePatch.expirationTimestamp !== undefined) {
      updates.expirationTimestamp = safePatch.expirationTimestamp;
    }
    await update(dbRef, updates);
  };

  const archivePunchCard = async (uid, punchCardId) => {
    if (!bizId || !uid || !punchCardId) return;
    const dbRef = ref(database, `/biz/punchcards/${bizId}/users/active/${uid}/${punchCardId}`);
    await update(dbRef, { archivedTimestamp: Date.now() });
  };

  const unarchivePunchCard = async (uid, punchCardId) => {
    if (!bizId || !uid || !punchCardId) return;
    const dbRef = ref(database, `/biz/punchcards/${bizId}/users/active/${uid}/${punchCardId}`);
    await update(dbRef, { archivedTimestamp: null });
  };

  const createSavedPunchCard = async (data) => {
    if (!bizId) throw new Error('Missing bizId');
    const dbRef = ref(database, `/biz/punchcards/${bizId}/savedPunchCards`);
    const newRef = push(dbRef);
    await update(newRef, {
      name: data.name || '',
      supportedAptsConfig: data.supportedAptsConfig || {
        supportsAll: true,
        supportedAptIds: [],
      },
      cardQuantity: Number(data.cardQuantity) || 1,
    });
    return newRef.key;
  };

  const updateSavedPunchCard = async (templateId, data) => {
    if (!bizId || !templateId) throw new Error('Missing ids');
    const dbRef = ref(database, `/biz/punchcards/${bizId}/savedPunchCards/${templateId}`);
    await update(dbRef, {
      name: data.name || '',
      supportedAptsConfig: data.supportedAptsConfig || {
        supportsAll: true,
        supportedAptIds: [],
      },
      cardQuantity: Number(data.cardQuantity) || 1,
    });
  };

  const deleteSavedPunchCard = async (templateId) => {
    if (!bizId || !templateId) return;
    const dbRef = ref(database, `/biz/punchcards/${bizId}/savedPunchCards/${templateId}`);
    await remove(dbRef);
  };

  return (
    <PunchCardsContext.Provider
      value={{
        punchCards,
        savedPunchCards,
        loading,
        punchCardsEnabled,
        getPunchCard,
        createPunchCard,
        updatePunchCard,
        archivePunchCard,
        unarchivePunchCard,
        createSavedPunchCard,
        updateSavedPunchCard,
        deleteSavedPunchCard,
        normalizePunchCardIds,
        punchCardTags,
        punchCardsTagsCount,
        hiddenPunchCardTagsKeys,
        getPunchCardAvailableTags,
        updatePunchCardTags,
        addPunchCardTag,
        modifyPunchCardTag,
      }}
    >
      {children}
    </PunchCardsContext.Provider>
  );
};

export const usePunchCards = () => useContext(PunchCardsContext) || {};
